import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '@src/components';
import {
  FiscalRepSubscriptionRowFragment,
  useCreateUpdateFiscalRepPaymentDetailsCheckoutUrlMutation,
} from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import { formatCurrency } from '@src/utils/string';
import { FiscalRepSubscriptionStatusInfo } from '../../utils/order-status';
import FiscalRepActions from './FiscalRepActions';
import { OrderItem } from './OrderItem';

export function OrderItemDetail({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-w-[150px] justify-between sm:flex-col sm:justify-normal sm:space-y-2">
      <strong>{title}</strong>
      <div>{children}</div>
    </div>
  );
}

// taken from https://stripe.com/docs/api/payment_methods/object
const PAYMENT_CARD_BRANDS: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'EFTPOS',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

export interface FiscalRepSubscriptionWithStatusInfo {
  row: FiscalRepSubscriptionRowFragment;
  statusInfo: FiscalRepSubscriptionStatusInfo;
}

export function FiscalRepSubscriptionItem({
  subscriptionWithInfo: { row: subscription, statusInfo },
  paymentColumns,
  compactView,
  className,
}: {
  subscriptionWithInfo: FiscalRepSubscriptionWithStatusInfo;
  paymentColumns?: React.ReactNode;
  compactView?: boolean;
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation('fiscal_rep');

  return (
    <OrderItem
      title={t('dashboard.order_title', 'Fiscal Representation for {{name}}', {
        name: `${subscription.givenNames} ${subscription.surname}`,
      })}
      statusInfo={statusInfo}
      actions={
        <FiscalRepActions
          statusInfo={statusInfo}
          subscriptionId={subscription.id}
        />
      }
      noBorder={compactView}
      className={className}
    >
      <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
        <OrderItemDetail title={t('dashboard.order_item.nif', 'NIF')}>
          {subscription.nifNumber}
        </OrderItemDetail>
        {paymentColumns}
        {subscription.cancelledAt && (
          <OrderItemDetail
            title={t('dashboard.order_item.cancelled_on', 'Cancelled On')}
          >
            {format(new Date(subscription.cancelledAt), 'yyyy-MM-dd')}
          </OrderItemDetail>
        )}
      </div>
    </OrderItem>
  );
}

export function FiscalRepPaymentColumns({
  subscriptionWithInfo: { row: subscription, statusInfo },
}: {
  subscriptionWithInfo: FiscalRepSubscriptionWithStatusInfo;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation('fiscal_rep');
  const [createUpdatePaymentsUrl] =
    useCreateUpdateFiscalRepPaymentDetailsCheckoutUrlMutation();
  const toast = useToast();

  if (!subscription.isActive) {
    return null;
  }

  const redirectToCheckout = (): void => {
    createUpdatePaymentsUrl({
      variables: {
        input: {
          fiscalRepSubscriptionId: subscription.id,
          locale: i18n.language,
        },
      },
    })
      .then((res) => {
        const result = res.data?.createUpdateFiscalRepPaymentDetailsCheckoutUrl;
        if (!result?.checkoutUrl) {
          throw new Error('No checkout url returned');
        }
        window.location.href = result.checkoutUrl;
      })
      .catch((err) => {
        toast.error(logAndFormatError(err));
      });
  };

  function getPaymentMethod(): string {
    if (subscription.renewalPaymentType === 'card') {
      const brand =
        PAYMENT_CARD_BRANDS[subscription.renewalCreditCardBrand || ''] ||
        t('order:payment_methods.card', 'Card');
      if (!subscription.renewalCreditCardLastFour) {
        return brand;
      }
      return t(
        'dashboard.order_item.payment_method_card',
        '{{brand}} ending in {{last4}}',
        {
          brand,
          last4: subscription.renewalCreditCardLastFour,
        },
      );
    }
    if (subscription.renewalPaymentType === 'link') {
      return 'Link';
    }
    return t('order:payment_methods.unknown', 'Unknown');
  }

  return (
    <>
      <OrderItemDetail title={t('dashboard.order_item.renews_on', 'Renews On')}>
        {format(new Date(subscription.renewalDateTime), 'yyyy-MM-dd')}
      </OrderItemDetail>
      {subscription.renewalAmountCents &&
        subscription.renewalAmountCurrency && (
          <OrderItemDetail title={t('dashboard.order_item.amount', 'Amount')}>
            {formatCurrency(
              subscription.renewalAmountCents,
              i18n.language,
              subscription.renewalAmountCurrency,
            )}{' '}
            {subscription.renewalAmountCurrency.toUpperCase()}
          </OrderItemDetail>
        )}
      <OrderItemDetail
        title={t('dashboard.order_item.payment_method', 'Payment Method')}
      >
        <div className="flex space-x-4">
          <div>{getPaymentMethod()}</div>
          {statusInfo.canUpdatePaymentMethod && (
            <LinkButton color="blue" onClick={redirectToCheckout}>
              {t('dashboard.order_item.update_payment_button', 'Update')}
            </LinkButton>
          )}
        </div>
      </OrderItemDetail>
    </>
  );
}

export function FiscalRepSubscriptionsItem({
  subscriptionsWithInfo,
}: {
  subscriptionsWithInfo: FiscalRepSubscriptionWithStatusInfo[];
}): JSX.Element | null {
  if (!subscriptionsWithInfo.length) {
    return null;
  }

  if (subscriptionsWithInfo.length === 1) {
    return (
      <FiscalRepSubscriptionItem
        subscriptionWithInfo={subscriptionsWithInfo[0]}
        paymentColumns={
          <FiscalRepPaymentColumns
            subscriptionWithInfo={subscriptionsWithInfo[0]}
          />
        }
      />
    );
  }

  return (
    <ul className="rounded-md bg-white shadow">
      <li className="flex flex-col gap-4 p-4 md:flex-row">
        <FiscalRepPaymentColumns
          subscriptionWithInfo={subscriptionsWithInfo[0]}
        />
      </li>
      {subscriptionsWithInfo.map((subscriptionWithInfo) => (
        <FiscalRepSubscriptionItem
          subscriptionWithInfo={subscriptionWithInfo}
          compactView
          key={subscriptionWithInfo.row.id}
          className="border-t"
        />
      ))}
    </ul>
  );
}
